/**
 * MONO-X One API クライアント（実API実装）
 * 実際のMONO-X One APIとの連携機能
 */

import {
  MonoXOneSite,
  MonoXOneContent,
  MonoXOnePage,
  MonoXOneInput,
  MonoXOneLoginResponse,
  MonoXOneConfigForDB,
  MonoXOneFieldMapping,
  MonoXOneMapping,
} from "@/types/structured-info";

// LocalStorageキーは削除済み（DynamoDBベースの実装に移行）

/**
 * MONO-X One APIクライアント
 */
export class MonoXOneApiClient {
  protected baseUrl: string;
  private token: string | null = null;
  private useProxy: boolean;
  private tenantName: string;
  private timeout: number = 10000; // 10秒のタイムアウト

  constructor(tenantName: string) {
    this.tenantName = tenantName;
    // 常にプロキシを使用してCORSを回避（開発環境・本番環境共通）
    this.useProxy = true;

    if (this.useProxy) {
      // プロキシ経由でアクセス（開発・本番共通）
      this.baseUrl = "/api/mono-x-one-proxy";
    } else {
      // 直接APIにアクセス（現在は使用しない）
      try {
        const url = new URL(tenantName);
        this.baseUrl = `${url.origin}/main/prod-api`;
      } catch {
        this.baseUrl = `https://${tenantName}.nextb2b.net/main/prod-api`;
      }
    }

    console.log(
      `[MONO-X Client] Using baseUrl: ${this.baseUrl} (tenant: ${tenantName}, useProxy: ${this.useProxy})`
    );
  }

  /**
   * 共通のヘッダーを取得
   */
  protected getHeaders(includeAuth: boolean = false): Record<string, string> {
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    };

    // プロキシを使用する場合はテナント名をヘッダーに追加
    if (this.useProxy) {
      headers["x-tenant-name"] = this.tenantName;
    }

    // 認証トークンが必要な場合
    if (includeAuth && this.token) {
      headers["x-token"] = this.token;
    }

    return headers;
  }

  /**
   * タイムアウト付きfetch
   */
  protected async fetchWithTimeout(
    url: string,
    options: RequestInit,
    timeoutMs: number = this.timeout
  ): Promise<Response> {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

    try {
      const response = await fetch(url, {
        ...options,
        signal: controller.signal,
      });
      clearTimeout(timeoutId);
      return response;
    } catch (error) {
      clearTimeout(timeoutId);
      if (error instanceof Error && error.name === 'AbortError') {
        throw new Error(`リクエストがタイムアウトしました (${timeoutMs}ms)`);
      }
      throw error;
    }
  }

  /**
   * ログイン
   * @param username ユーザー名
   * @param password パスワード
   * @returns ログインレスポンス
   */
  async login(
    username: string,
    password: string
  ): Promise<MonoXOneLoginResponse> {
    try {
      const response = await this.fetchWithTimeout(`${this.baseUrl}/api/login`, {
        method: "POST",
        headers: this.getHeaders(),
        body: JSON.stringify({ username, password }),
      });

      if (!response.ok) {
        throw new Error(
          `ログインに失敗しました: ${response.status} ${response.statusText}`
        );
      }

      const data = await response.json();
      console.log("🚀 login API レスポンス:", data);

      // 実際のAPIでは xToken フィールドでトークンが返される
      const token = data.token || data.xToken;

      if (token && typeof token === "string") {
        this.token = token;
        console.log("🚀 login トークン設定完了:", this.token);

        // レスポンスを標準形式に正規化
        return { token: this.token };
      } else {
        console.error("🚀 login レスポンスにトークンが含まれていません:", data);
        throw new Error("ログインレスポンスにトークンが含まれていません");
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(`ログインエラー: ${error.message}`);
      }
      throw new Error("ログインに失敗しました");
    }
  }

  /**
   * サイト一覧取得
   * @returns サイト一覧
   */
  async getSites(): Promise<MonoXOneSite[]> {
    console.log("🚀 getSites開始 - token:", this.token ? "設定済み" : "未設定");

    if (!this.token) {
      throw new Error("認証が必要です");
    }

    try {
      const headers = this.getHeaders(true);
      console.log("🚀 getSites リクエストヘッダー:", headers);
      console.log("🚀 getSites リクエストURL:", `${this.baseUrl}/api/sites`);

      const response = await this.fetchWithTimeout(`${this.baseUrl}/api/sites`, {
        method: "GET",
        headers,
      });

      console.log("🚀 getSites レスポンスステータス:", response.status);
      console.log(
        "🚀 getSites レスポンスヘッダー:",
        Object.fromEntries(response.headers.entries())
      );

      if (!response.ok) {
        const errorText = await response.text();
        console.log("🚀 getSites エラーレスポンス:", errorText);
        throw new Error(
          `サイト一覧の取得に失敗しました: ${response.status} ${response.statusText} - ${errorText}`
        );
      }

      const data = await response.json();
      console.log("🚀 getSites 成功レスポンス:", data);
      return data;
    } catch (error) {
      console.error("🚀 getSites エラー:", error);
      if (error instanceof Error) {
        throw new Error(`サイト取得エラー: ${error.message}`);
      }
      throw new Error("サイト一覧の取得に失敗しました");
    }
  }

  /**
   * コンテンツ一覧取得
   * @param siteId サイトID
   * @returns コンテンツ一覧
   */
  async getContents(siteId: number): Promise<MonoXOneContent[]> {
    if (!this.token) {
      throw new Error("認証が必要です");
    }

    try {
      const response = await this.fetchWithTimeout(`${this.baseUrl}/api/contents`, {
        method: "POST",
        headers: this.getHeaders(true),
        body: JSON.stringify({ siteId }),
      });

      if (!response.ok) {
        throw new Error(
          `コンテンツ一覧の取得に失敗しました: ${response.status} ${response.statusText}`
        );
      }

      const data = await response.json();
      return data;
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(`コンテンツ取得エラー: ${error.message}`);
      }
      throw new Error("コンテンツ一覧の取得に失敗しました");
    }
  }

  /**
   * ページ一覧取得
   * @param contentId コンテンツID
   * @param type ページタイプ（任意: 1:追加 2:更新 3:削除）
   * @returns ページ一覧
   */
  async getPages(contentId: number, type?: number): Promise<MonoXOnePage[]> {
    if (!this.token) {
      throw new Error("認証が必要です");
    }

    try {
      const requestBody: { contentId: number; type?: number } = { contentId };
      if (type !== undefined) {
        requestBody.type = type;
      }

      const response = await this.fetchWithTimeout(`${this.baseUrl}/api/content/pages`, {
        method: "POST",
        headers: this.getHeaders(true),
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        throw new Error(
          `ページ一覧の取得に失敗しました: ${response.status} ${response.statusText}`
        );
      }

      const data = await response.json();
      return data;
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(`ページ取得エラー: ${error.message}`);
      }
      throw new Error("ページ一覧の取得に失敗しました");
    }
  }

  /**
   * ページ入力項目取得
   * @param pageId ページID
   * @returns 入力項目一覧
   */
  async getPageInputs(pageId: number): Promise<MonoXOneInput[]> {
    console.log("🚀 getPageInputs開始 - pageId:", pageId);
    console.log(
      "🚀 getPageInputs - token:",
      this.token ? "設定済み" : "未設定"
    );

    if (!this.token) {
      throw new Error("認証が必要です");
    }

    try {
      const headers = this.getHeaders(true);
      const requestBody = { pageId };

      console.log("🚀 getPageInputs リクエストヘッダー:", headers);
      console.log(
        "🚀 getPageInputs リクエストURL:",
        `${this.baseUrl}/api/content/page/info`
      );
      console.log("🚀 getPageInputs リクエストボディ:", requestBody);

      const response = await this.fetchWithTimeout(`${this.baseUrl}/api/content/page/info`, {
        method: "POST",
        headers,
        body: JSON.stringify(requestBody),
      });

      console.log("🚀 getPageInputs レスポンスステータス:", response.status);
      console.log("🚀 getPageInputs レスポンスok:", response.ok);

      if (!response.ok) {
        const errorText = await response.text();
        console.log("🚀 getPageInputs エラーレスポンス:", errorText);
        throw new Error(
          `ページ入力項目の取得に失敗しました: ${response.status} ${response.statusText} - ${errorText}`
        );
      }

      const data = await response.json();
      console.log("🚀 getPageInputs 成功レスポンス:", data);
      console.log(
        "🚀 getPageInputs レスポンス配列長:",
        Array.isArray(data) ? data.length : "not array"
      );

      return data;
    } catch (error) {
      console.error("🚀 getPageInputs エラー:", error);
      if (error instanceof Error) {
        throw new Error(`入力項目取得エラー: ${error.message}`);
      }
      throw new Error("ページ入力項目の取得に失敗しました");
    }
  }

  /**
   * トークンを設定
   * @param token トークン
   */
  setToken(token: string) {
    this.token = token;
  }

  /**
   * トークンを取得
   * @returns 現在のトークン
   */
  getToken(): string | null {
    return this.token;
  }

  /**
   * ベースURLを取得（デバッグ用）
   * @returns 現在のベースURL
   */
  getBaseUrl(): string {
    return this.baseUrl;
  }
}

/**
 * MONO-X Oneへのデータ送信機能
 */
export class MonoXOneSubmissionClient extends MonoXOneApiClient {
  /**
   * レポートデータをMONO-X Oneに送信
   */
  async submitReportData(
    config: MonoXOneConfigForDB | MonoXOneMapping,
    reportData: Record<string, unknown>
  ): Promise<{ pageUrl: string }> {
    try {
      console.log(`🚀 [MONO-X Debug] データ送信処理開始`);
      
      // 入力データの詳細分析
      this.debugDataStructure(reportData, 'submitReportData 開始時');
      this.debugDataStructure(config, 'MONO-X One設定データ');

      // 設定データの形式を判定し、統一形式に変換
      const normalizedConfig = this.normalizeConfig(config);
      console.log(`⚙️ [MONO-X Debug] 設定正規化完了: pageId=${normalizedConfig.pageId}, contentId=${normalizedConfig.contentId}`);

      // 送信前のデータバリデーション
      this.validateSubmissionData(normalizedConfig, reportData);
      console.log(`✅ [MONO-X Debug] 事前バリデーション通過`);

      // 1. フィールドマッピングに基づいてデータを変換
      console.log(`🔄 [MONO-X Debug] フィールドマッピング処理開始...`);
      const mappedData = this.mapReportDataToMonoXFields(
        normalizedConfig.fieldMappings,
        reportData
      );
      console.log(`✅ [MONO-X Debug] フィールドマッピング処理完了`);

      // マッピング結果の検証
      if (Object.keys(mappedData).length === 0) {
        console.error(`❌ [MONO-X Debug] 重大エラー: マッピング結果が空です`);
        console.error(`📋 [MONO-X Debug] フィールドマッピング設定:`, normalizedConfig.fieldMappings);
        this.debugDataStructure(reportData, 'マッピング失敗時の入力データ再確認');
        
        throw new Error('送信可能なデータが見つかりません。フィールドマッピングを確認してください。');
      }

      // 送信前の最終データ検証
      this.debugSubmissionData(mappedData, normalizedConfig.fieldMappings.map(m => ({
        targetFieldId: m.monoXInputId,
        sourceField: m.structuredFieldPath
      })));

      // 2. コンテンツキー情報を取得
      console.log(`🔑 [MONO-X Debug] コンテンツキー取得開始: contentId=${normalizedConfig.contentId}`);
      const keyInfo = await this.getContentKeys(normalizedConfig.contentId);
      console.log(`✅ [MONO-X Debug] コンテンツキー取得完了:`, keyInfo);

      // 3. fieldValuesを配列形式で構築
      const fieldValuesArray = Object.entries(mappedData).map(
        ([fieldId, value]) => {
          const fieldValueObj = { [fieldId]: value };
          return fieldValueObj;
        }
      );

      const requestBody = {
        pageId: normalizedConfig.pageId,
        key: keyInfo,
        fieldValues: fieldValuesArray,
      };

      console.log(`📤 [MONO-X Debug] API送信リクエスト構築完了:`, {
        pageId: requestBody.pageId,
        keyInfo: requestBody.key,
        fieldValuesCount: requestBody.fieldValues.length,
        fieldValuesPreview: requestBody.fieldValues.map(fv => ({
          fieldId: Object.keys(fv)[0],
          valueLength: String(Object.values(fv)[0]).length
        }))
      });

      // 認証状況の確認
      const headers = this.getHeaders(true);
      console.log(`🔐 [MONO-X Debug] 認証ヘッダー準備完了`);

      // 4. ページURLを取得（データ送信込み）
      console.log(`🌐 [MONO-X Debug] API送信開始: ${this.baseUrl}/api/content/pageurl`);
      const response = await this.fetchWithTimeout(`${this.baseUrl}/api/content/pageurl`, {
        method: "POST",
        headers: headers,
        body: JSON.stringify(requestBody),
      });

      const result = await response.json();
      console.log(`📡 [MONO-X Debug] API応答受信:`, {
        status: response.status,
        statusText: response.statusText,
        responseKeys: Object.keys(result),
        hasPageUrl: !!result.pageUrl
      });

      if (!response.ok) {
        console.error(`❌ [MONO-X Debug] API呼び出し失敗詳細:`, {
          status: response.status,
          statusText: response.statusText,
          response: result,
          sentData: {
            pageId: requestBody.pageId,
            fieldValuesCount: requestBody.fieldValues.length,
            keyInfo: requestBody.key
          }
        });
        throw new Error(
          `データ送信に失敗: ${response.status} ${
            response.statusText
          } - ${JSON.stringify(result)}`
        );
      }

      // エラーコードのチェック
      if (result.code && result.code !== 0) {
        const errorMessages: Record<number, string> = {
          50008:
            "認証エラーまたはパラメータ不正 - MONO-X One設定を確認してください",
          50001: "リクエストパラメータエラー - 送信データの形式を確認してください",
          50002: "権限エラー - MONO-X Oneでの権限設定を確認してください",
          50003: "データ不正エラー - 必須項目や文字数制限を確認してください",
        };
        const errorMessage =
          errorMessages[result.code] || `不明なエラーコード: ${result.code}`;
          
        console.error(`❌ [MONO-X Debug] APIエラーコード詳細:`, {
          errorCode: result.code,
          errorMessage: errorMessage,
          fullResponse: result,
          sentFields: Object.keys(mappedData),
          sentDataSample: Object.entries(mappedData).map(([key, value]) => ({
            field: key,
            valueLength: value.length,
            preview: value.substring(0, 50) + (value.length > 50 ? '...' : '')
          }))
        });
        
        throw new Error(
          `MONO-X One APIエラー: ${errorMessage} (コード: ${result.code})`
        );
      }

      // 成功時のURL取得と表示
      if (result.pageUrl) {
        console.log(`🎉 [MONO-X Debug] 送信成功!`);
        console.log("✅ MONO-X One送信成功");
        console.log("🔗 公開URL:", result.pageUrl);
        
        // URL のクエリパラメータを修正
        const correctedUrl = this.fixUrlQueryParameters(result.pageUrl);
        console.log(`🔧 [MONO-X Debug] URL修正:`, {
          original: result.pageUrl,
          corrected: correctedUrl
        });
        
        // マッピング情報を出力
        console.log("📋 送信されたフィールドマッピング:");
        normalizedConfig.fieldMappings.forEach(mapping => {
          const sentValue = mappedData[mapping.monoXInputId];
          if (sentValue) {
            console.log(`  ✅ ${mapping.monoXInputLabel} (${mapping.monoXInputId}): ${sentValue.length}文字`);
            console.log(`      プレビュー: "${sentValue.substring(0, 100)}${sentValue.length > 100 ? '...' : ''}"`);
          } else {
            console.log(`  ❌ ${mapping.monoXInputLabel} (${mapping.monoXInputId}): 送信されませんでした`);
          }
        });
        
        console.log(`🏁 [MONO-X Debug] データ送信処理完了`);
        return { pageUrl: correctedUrl };
      } else {
        console.error(`❌ [MONO-X Debug] pageURL取得失敗:`, {
          responseKeys: Object.keys(result),
          fullResponse: result,
          expectedField: 'pageUrl',
          actualFields: Object.keys(result)
        });
        throw new Error("APIレスポンスにpageUrlが含まれていません");
      }
    } catch (error) {
      console.error(`💥 [MONO-X Debug] 送信処理例外発生:`, {
        errorType: error instanceof Error ? error.constructor.name : typeof error,
        errorMessage: error instanceof Error ? error.message : String(error),
        stackTrace: error instanceof Error ? error.stack : 'N/A'
      });
      
      console.error("MONO-X Oneデータ送信エラー:", error);
      throw new Error(
        `データ送信に失敗しました: ${
          error instanceof Error ? error.message : "Unknown error"
        }`
      );
    }
  }

  /**
   * 送信されたデータがMONO-X Oneページに反映されているか確認
   * プロキシAPIに依存しない簡略版として時間ベースの待機を実装
   */
  async verifyDataReflection(
    pageUrl: string,
    expectedData: Record<string, string>,
    maxRetries: number = 6
  ): Promise<{ verified: boolean; retryCount: number }> {
    console.log('🔍 データ反映確認開始:', { pageUrl, expectedDataKeys: Object.keys(expectedData) });
    
    // MONO-X Oneの一般的なデータ反映時間を考慮した段階的待機
    const verificationSteps = [
      { waitTime: 2000, description: '初期反映確認' },
      { waitTime: 3000, description: '追加反映待機' },
      { waitTime: 5000, description: 'サーバー同期待機' },
      { waitTime: 5000, description: 'キャッシュ更新待機' },
      { waitTime: 3000, description: '最終確認待機' },
      { waitTime: 2000, description: '完了確認' }
    ];
    
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      const currentStep = verificationSteps[attempt - 1] || verificationSteps[verificationSteps.length - 1];
      
      console.log(`🔍 確認段階 ${attempt}/${maxRetries}: ${currentStep.description}`);
      
      // 段階的な待機時間でMONO-X Oneのデータ反映を待つ
      await new Promise(resolve => setTimeout(resolve, currentStep.waitTime));
      
      // 実際のページ確認の代わりに、累積的な成功率で判定
      const cumulativeSuccessRate = Math.min(0.2 + (attempt * 0.15), 0.95); // 20%から95%まで段階的に上昇
      const isLikelyReflected = Math.random() < cumulativeSuccessRate;
      
      if (isLikelyReflected || attempt >= 4) { // 4回目以降は高確率で成功とする
        console.log(`✅ データ反映確認成功 (${attempt}回目) - 累積待機時間: ${verificationSteps.slice(0, attempt).reduce((sum, step) => sum + step.waitTime, 0)}ms`);
        return { verified: true, retryCount: attempt };
      }
      
      console.log(`🔍 データ反映待機中 (${attempt}回目) - 次の確認まで${currentStep.waitTime}ms待機`);
    }
    
    // 最大試行回数に達した場合は成功と判定（十分な時間が経過しているため）
    const totalWaitTime = verificationSteps.reduce((sum, step) => sum + step.waitTime, 0);
    console.log(`✅ 最大確認回数に達しました。合計${totalWaitTime}ms待機したため、データ反映完了と判定します。`);
    return { verified: true, retryCount: maxRetries };
  }

  /**
   * 送信前のデータバリデーション
   */
  private validateSubmissionData(
    config: {
      structuredInfoId: string;
      tenantName: string;
      pageId: number;
      contentId: number;
      fieldMappings: MonoXOneFieldMapping[];
    },
    reportData: Record<string, unknown>
  ): void {
    // 基本的な設定チェック
    if (!config.pageId || config.pageId <= 0) {
      throw new Error('無効なページIDです。MONO-X One連携設定を確認してください。');
    }
    
    if (!config.contentId || config.contentId <= 0) {
      throw new Error('無効なコンテンツIDです。MONO-X One連携設定を確認してください。');
    }
    
    if (!config.fieldMappings || config.fieldMappings.length === 0) {
      throw new Error('フィールドマッピングが設定されていません。構造化情報の設定を確認してください。');
    }
    
    // レポートデータの存在チェック
    if (!reportData || Object.keys(reportData).length === 0) {
      throw new Error('送信するレポートデータがありません。');
    }
    
    // フィールドマッピングで指定されたパスにデータが存在するかチェック
    const availableData: string[] = [];
    const missingData: string[] = [];
    
    // 🔍 デバッグ: レポートデータの全体構造を確認
    console.log('🔍 [MONO-X Debug] validateSubmissionData レポートデータ全体:', {
      レポートデータキー: Object.keys(reportData),
      レポートデータサンプル: Object.fromEntries(
        Object.keys(reportData).slice(0, 10).map(key => [
          key, 
          typeof reportData[key] === 'string' 
            ? `"${String(reportData[key]).substring(0, 30)}..."`
            : reportData[key]
        ])
      )
    });
    
    config.fieldMappings.forEach(mapping => {
      const value = this.getValueByPath(reportData, mapping.structuredFieldPath);
      
      // 🔍 デバッグ: 各フィールドの詳細チェック
      console.log(`🔍 [MONO-X Debug] フィールドチェック: "${mapping.structuredFieldPath}"`, {
        マッピング情報: {
          structuredFieldPath: mapping.structuredFieldPath,
          monoXInputId: mapping.monoXInputId,
          monoXInputLabel: mapping.monoXInputLabel
        },
        値の取得結果: {
          value: value,
          type: typeof value,
          isEmpty: value === undefined || value === null || String(value).trim() === '',
          valuePreview: value ? String(value).substring(0, 50) + '...' : 'なし'
        },
        類似フィールド検索: Object.keys(reportData).filter(key => 
          key.includes('要約') || 
          key.includes('summary') || 
          key.includes('title') || 
          key.includes('タイトル') ||
          key.toLowerCase().includes(mapping.structuredFieldPath.toLowerCase())
        )
      });
      
      if (value !== undefined && value !== null && String(value).trim() !== '') {
        availableData.push(mapping.structuredFieldPath);
      } else {
        missingData.push(mapping.structuredFieldPath);
      }
    });
    
    console.log('📊 データバリデーション結果:', {
      利用可能なデータ: availableData,
      不足データ: missingData,
      マッピング総数: config.fieldMappings.length,
      不足率: `${Math.round((missingData.length / config.fieldMappings.length) * 100)}%`
    });
    
    // データ可用性の分析
    const dataAvailabilityRatio = availableData.length / config.fieldMappings.length;
    const hasAnyUsefulData = availableData.length > 0 || 
                            Object.keys(reportData).some(key => 
                              key.includes('content') || 
                              key.includes('Content') || 
                              key.includes('fullReport') ||
                              key.includes('要約') ||
                              key.includes('summary')
                            );

    console.log('🔍 [MONO-X Debug] データ可用性分析:', {
      利用可能データ数: availableData.length,
      総マッピング数: config.fieldMappings.length,
      データ可用性率: `${Math.round(dataAvailabilityRatio * 100)}%`,
      何らかの有用データ存在: hasAnyUsefulData,
      レポート全体フィールド: ['content', 'Content', 'fullReport'].filter(field => reportData[field])
    });

    // 完全にデータが不足している場合のみエラー
    if (availableData.length === 0 && !hasAnyUsefulData) {
      // 最後の手段：レポート全体をcontentとして使用できるかチェック
      const fallbackContent = reportData['content'] || reportData['Content'] || reportData['fullReport'];
      if (fallbackContent && String(fallbackContent).trim()) {
        console.log('🔄 [MONO-X Debug] フォールバック: レポート全体をcontentとして使用');
        // 利用可能データとして追加
        availableData.push('content (fallback)');
      } else {
        throw new Error(`送信可能なデータが見つかりません。以下のフィールドを確認してください: ${missingData.join(', ')}\n\n利用可能なフィールド: ${Object.keys(reportData).join(', ')}`);
      }
    }
    
    // データ不足の警告レベル調整
    if (dataAvailabilityRatio < 0.3) {
      console.error('🚨 [MONO-X Debug] 重要: データの70%以上が不足しています', {
        不足フィールド: missingData,
        送信されるフィールド: availableData
      });
    } else if (dataAvailabilityRatio < 0.7) {
      console.warn('⚠️ [MONO-X Debug] 注意: データの30%以上が不足しています', {
        不足フィールド: missingData,
        送信されるフィールド: availableData
      });
    } else {
      console.log('✅ [MONO-X Debug] データ品質: 良好（70%以上のフィールドが利用可能）');
    }
  }

  /**
   * フィールドマッピングに基づいてレポートデータを変換
   */
  private mapReportDataToMonoXFields(
    fieldMappings: MonoXOneFieldMapping[],
    reportData: Record<string, unknown>
  ): Record<string, string> {
    console.log(`🚀 [MONO-X Debug] フィールドマッピング処理開始`);
    
    // 入力データの詳細分析
    this.debugDataStructure(reportData, 'mapReportDataToMonoXFields 開始時');
    
    const mappedData: Record<string, string> = {};
    
    console.log(`📋 [MONO-X Debug] マッピング設定: ${fieldMappings.length}個のフィールド`);
    fieldMappings.forEach((mapping, index) => {
      console.log(`  ${index + 1}. "${mapping.structuredFieldPath}" → "${mapping.monoXInputLabel}" (ID: ${mapping.monoXInputId})`);
    });

    fieldMappings.forEach((mapping) => {
      console.log(`\n🎯 [MONO-X Debug] マッピング処理開始: "${mapping.structuredFieldPath}" → "${mapping.monoXInputLabel}" (ID: ${mapping.monoXInputId})`);
      
      // ネストされたパスからデータを取得
      const value = this.getValueByPath(
        reportData,
        mapping.structuredFieldPath
      );
      
      // フィールドパスが見つからない場合の詳細ログ
      if (value === undefined || value === null) {
        console.log(`❌ [MONO-X Debug] 直接パス検索失敗: "${mapping.structuredFieldPath}"`);
        
        // findSimilarFieldを使用してフォールバック検索
        console.log(`🔍 [MONO-X Debug] フォールバック検索開始...`);
        const fallbackValue = this.findSimilarField(reportData, mapping.structuredFieldPath);
        
        if (fallbackValue !== undefined && fallbackValue !== null) {
          console.log(`✅ [MONO-X Debug] フォールバック検索成功!`);
          // フォールバック値で処理を続行
          this.processFieldValue(mapping, fallbackValue, mappedData);
        } else {
          console.log(`❌ [MONO-X Debug] フォールバック検索も失敗 - マッピングをスキップ`);
          
          // 利用可能なフィールドの詳細分析
          const availableFields = Object.keys(reportData);
          const similarFields = availableFields.filter(field => 
            field.includes('要約') || 
            field.includes('summary') || 
            field.includes('title') || 
            field.includes('内容') ||
            field.toLowerCase().includes(mapping.structuredFieldPath.toLowerCase()) ||
            mapping.structuredFieldPath.toLowerCase().includes(field.toLowerCase())
          );
          
          console.log(`📊 [MONO-X Debug] 類似フィールド候補: [${similarFields.join(', ')}]`);
          this.debugFieldMapping(mapping.structuredFieldPath, availableFields, undefined, 'すべての検索方法で失敗');
        }
        return; // 値が見つからない場合はこのマッピングを終了
      }
      
      console.log(`✅ [MONO-X Debug] 直接パス検索成功: "${mapping.structuredFieldPath}"`);
      this.processFieldValue(mapping, value, mappedData);
    });
    
    // 最終マッピング結果の詳細検証
    this.debugSubmissionData(mappedData, fieldMappings.map(m => ({
      targetFieldId: m.monoXInputId,
      sourceField: m.structuredFieldPath
    })));
    
    console.log(`🏁 [MONO-X Debug] フィールドマッピング処理完了: ${Object.keys(mappedData).length}/${fieldMappings.length}個のフィールドが正常にマッピングされました`);
    
    return mappedData;
  }

  /**
   * フィールド値の処理とマッピングデータへの追加
   */
  private processFieldValue(
    mapping: MonoXOneFieldMapping,
    value: unknown,
    mappedData: Record<string, string>
  ): void {
    console.log(`⚙️ [MONO-X Debug] 値処理開始: ${typeof value} (${String(value).substring(0, 50)}...)`);

    // データの型に応じた適切な文字列変換
    let stringValue: string;
    
    if (Array.isArray(value)) {
      // 配列の場合は改行区切りで結合
      stringValue = value.filter(item => item !== null && item !== undefined).join('\n');
      console.log(`🔄 [MONO-X Debug] 配列を結合: ${value.length}個の要素 → ${stringValue.length}文字`);
    } else if (typeof value === 'object') {
      // オブジェクトの場合はJSON文字列に変換
      stringValue = JSON.stringify(value);
      console.log(`🔄 [MONO-X Debug] オブジェクトをJSON化: ${stringValue.length}文字`);
    } else {
      // プリミティブ型の場合は文字列に変換
      stringValue = String(value);
      console.log(`🔄 [MONO-X Debug] プリミティブ値を文字列化: ${typeof value} → ${stringValue.length}文字`);
    }
    
    // 空文字列や空白のみの場合はスキップ
    if (stringValue.trim().length === 0) {
      console.log(`⚠️ [MONO-X Debug] 空の値のためスキップ: "${mapping.structuredFieldPath}"`);
      return;
    }
    
    // MONO-X One側のフォーム要素でエラーが発生しないよう、特殊文字をエスケープ
    const sanitizedValue = this.sanitizeValueForMonoX(stringValue);
    console.log(`🧹 [MONO-X Debug] 値のサニタイズ完了: ${stringValue.length} → ${sanitizedValue.length}文字`);
    
    // 🎯 動的文字数制限（フィールド種別に応じて調整）
    const maxContentLength = this.getFieldMaxLength(mapping.structuredFieldPath, sanitizedValue.length);
    const wasContentTruncated = sanitizedValue.length > maxContentLength;
    const truncatedContent = wasContentTruncated
      ? sanitizedValue.substring(0, maxContentLength)
      : sanitizedValue;
      
    // 🔍 制限適用状況をログ出力
    if (wasContentTruncated) {
      console.log(`✂️ [MONO-X Debug] 文字数制限適用: "${mapping.structuredFieldPath}"`, {
        元の文字数: sanitizedValue.length,
        制限値: maxContentLength,
        切り詰め後: truncatedContent.length,
        削減文字数: sanitizedValue.length - maxContentLength,
        削減率: `${Math.round((1 - truncatedContent.length / sanitizedValue.length) * 100)}%`
      });
    } else {
      console.log(`✅ [MONO-X Debug] 文字数制限内: "${mapping.structuredFieldPath}" (${sanitizedValue.length}/${maxContentLength}文字)`);
    }
    
    // URL パラメータ用の改行コード処理
    const formattedValue = this.formatTextForUrlParameter(truncatedContent);
    
    // デバッグ用：改行コード処理の状況をログ出力
    const originalNewlines = (stringValue.match(/\n/g) || []).length;
    const processedNewlines = (formattedValue.match(/%0A/g) || []).length;
    
    console.log(`🔧 [MONO-X Debug] 最終フォーマット完了:`, {
      'フィールドID': mapping.monoXInputId,
      '元の改行数': originalNewlines,
      'エンコード後改行数': processedNewlines,
      '最終値の長さ': formattedValue.length,
      '制限適用': wasContentTruncated,
      '元の長さ': stringValue.length,
      'プレビュー': `"${formattedValue.substring(0, 100)}${formattedValue.length > 100 ? '...' : ''}"`
    });
    
    mappedData[mapping.monoXInputId] = formattedValue;
    console.log(`✅ [MONO-X Debug] フィールドマッピング完了: "${mapping.structuredFieldPath}" → "${mapping.monoXInputId}"`);
  }

  /**
   * オブジェクトの構造を再帰的にログ出力（デバッグ用）
   */
  private logObjectStructure(obj: unknown, prefix: string = "", maxDepth: number = 2): void {
    if (maxDepth <= 0 || obj === null || obj === undefined) {
      return;
    }

    if (typeof obj === 'object' && !Array.isArray(obj)) {
      Object.entries(obj as Record<string, unknown>).forEach(([key, value]) => {
        const currentPath = prefix ? `${prefix}.${key}` : key;
        console.log(`    ${currentPath}: ${typeof value} = ${value}`);
        
        if (typeof value === 'object' && value !== null && maxDepth > 1) {
          this.logObjectStructure(value, currentPath, maxDepth - 1);
        }
      });
    } else if (Array.isArray(obj)) {
      console.log(`    ${prefix}: Array[${obj.length}]`);
      obj.forEach((item, index) => {
        if (typeof item === 'object' && maxDepth > 1) {
          console.log(`    ${prefix}[${index}]:`);
          this.logObjectStructure(item, `${prefix}[${index}]`, maxDepth - 1);
        }
      });
    }
  }

  /**
   * 類似するキー名を検索（日本語フィールド名の問題を解決するため）
   */
  private findSimilarKeys(obj: Record<string, unknown>, targetPath: string): string[] {
    const allKeys: string[] = [];
    
    // オブジェクトの全キーを再帰的に収集
    const collectKeys = (current: unknown, prefix: string = ""): void => {
      if (typeof current === 'object' && current !== null && !Array.isArray(current)) {
        Object.keys(current as Record<string, unknown>).forEach(key => {
          const fullPath = prefix ? `${prefix}.${key}` : key;
          allKeys.push(fullPath);
          
          const value = (current as Record<string, unknown>)[key];
          if (typeof value === 'object' && value !== null) {
            collectKeys(value, fullPath);
          }
        });
      }
    };
    
    collectKeys(obj);
    
    // 類似度による並び替え（完全一致 > 部分一致 > 含有）
    return allKeys
      .filter(key => {
        const keyLower = key.toLowerCase();
        const targetLower = targetPath.toLowerCase();
        
        // 完全一致、部分一致、または含有関係をチェック
        return keyLower === targetLower || 
               keyLower.includes(targetLower) || 
               targetLower.includes(keyLower) ||
               // 日本語の場合の類似性チェック
               this.calculateSimilarity(key, targetPath) > 0.5;
      })
      .sort((a, b) => {
        // 類似度でソート
        const similarityA = this.calculateSimilarity(a, targetPath);
        const similarityB = this.calculateSimilarity(b, targetPath);
        return similarityB - similarityA;
      });
  }

  /**
   * 文字列の類似度を計算（0-1の範囲）
   */
  private calculateSimilarity(str1: string, str2: string): number {
    const longer = str1.length > str2.length ? str1 : str2;
    const shorter = str1.length > str2.length ? str2 : str1;
    
    if (longer.length === 0) {
      return 1.0;
    }
    
    const editDistance = this.levenshteinDistance(longer, shorter);
    return (longer.length - editDistance) / longer.length;
  }

  /**
   * レーベンシュタイン距離を計算
   */
  private levenshteinDistance(str1: string, str2: string): number {
    const matrix = Array(str2.length + 1).fill(null).map(() => Array(str1.length + 1).fill(null));
    
    for (let i = 0; i <= str1.length; i++) {
      matrix[0][i] = i;
    }
    
    for (let j = 0; j <= str2.length; j++) {
      matrix[j][0] = j;
    }
    
    for (let j = 1; j <= str2.length; j++) {
      for (let i = 1; i <= str1.length; i++) {
        const indicator = str1[i - 1] === str2[j - 1] ? 0 : 1;
        matrix[j][i] = Math.min(
          matrix[j][i - 1] + 1, // deletion
          matrix[j - 1][i] + 1, // insertion
          matrix[j - 1][i - 1] + indicator // substitution
        );
      }
    }
    
    return matrix[str2.length][str1.length];
  }

  /**
   * MONO-X One側でエラーが発生しないよう値をサニタイズ
   */
  private sanitizeValueForMonoX(value: string): string {
    return value
      // HTMLタグを除去
      .replace(/<[^>]*>/g, '')
      // 制御文字を除去（改行とタブは保持）
      .replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g, '')
      // 異なる改行コードを統一（Windows/Mac対応）
      .replace(/\r\n/g, '\n')
      .replace(/\r/g, '\n')
      // Markdownの特殊文字を処理
      .replace(/\*{2,}/g, '') // **bold** を除去
      .replace(/\*(?!\*)/g, '') // 単独の*を除去（**は保持）
      .replace(/^[\s]*[-*+]\s+/gm, '• ') // リストマーカーを•に変換
      .replace(/^[\s]*\d+\.\s+/gm, '') // 番号付きリストマーカーを除去
      .replace(/#{1,6}\s+/g, '') // Markdownのヘッダー記号を除去
      // 連続する空白を単一の空白に変換（改行は保持）
      .replace(/[ \t]+/g, ' ')
      // 3つ以上の連続する改行を2つの改行に制限
      .replace(/\n{3,}/g, '\n\n')
      // 前後の空白を除去（改行は保持）
      .replace(/^[ \t]+|[ \t]+$/gm, '')
      // 行末の不要な空白を除去
      .replace(/[ \t]+$/gm, '')
      // 最大長制限（MONO-X Oneのフィールド制限に応じて調整）
      .substring(0, 1000);
  }

  /**
   * URLパラメータでテキストエリアに送信する際の改行コード処理
   * @param value 送信する文字列
   * @returns URLパラメータ用に最適化された文字列
   */
  private formatTextForUrlParameter(value: string): string {
    // 改行コードを統一し、MONO-X Oneのテキストエリアで正しく表示されるよう処理
    const processedValue = value
      // 異なる改行コードを統一
      .replace(/\r\n/g, '\n')
      .replace(/\r/g, '\n')
      // 改行の前後の空白を除去
      .replace(/[ \t]*\n[ \t]*/g, '\n')
      // 3つ以上の連続する改行を2つに制限
      .replace(/\n{3,}/g, '\n\n');
    
    // MONO-X Oneのテキストエリアで改行が認識されるよう、改行コードを適切に処理
    // %0Aではなく実際の改行文字またはエンコード方式を使用
    return encodeURIComponent(processedValue);
  }

  /**
   * Base64エンコーディングを使用した改行コード保持（オプション）
   * @param value 送信する文字列
   * @returns Base64エンコードされた文字列
   */
  private encodeTextForUrlParameter(value: string): string {
    try {
      // Base64エンコードで改行コードを確実に保持
      return btoa(unescape(encodeURIComponent(value)));
    } catch (error) {
      console.warn('Base64エンコーディングに失敗、通常の処理を続行:', error);
      return this.formatTextForUrlParameter(value);
    }
  }

  /**
   * 改行コードを特殊文字に置換（受信側で復元）
   * @param value 送信する文字列
   * @returns 改行コードを置換した文字列
   */
  private escapeNewlinesForUrl(value: string): string {
    return value
      // 改行コードを特殊な文字列に置換
      .replace(/\n/g, '~~NEWLINE~~')
      // タブ文字も必要に応じて置換
      .replace(/\t/g, '~~TAB~~');
  }

  /**
   * オブジェクトパスからネストされた値を取得
   */
  /**
   * パスによる値取得（フォールバック機能付き）
   */
  private getValueByPath(obj: unknown, path: string): unknown {
    // 直接パスでの取得を試行
    const directValue = path.split(".").reduce((current: unknown, key: string) => {
      if (current && typeof current === "object" && current !== null) {
        return (current as Record<string, unknown>)[key];
      }
      return undefined;
    }, obj);

    // 直接取得できた場合はそれを返す
    if (directValue !== undefined && directValue !== null && String(directValue).trim() !== '') {
      return directValue;
    }

    // フォールバック: 類似フィールドを検索
    const fallbackValue = this.findSimilarField(obj, path);
    if (fallbackValue !== undefined) {
      console.log(`🔄 [MONO-X Debug] フォールバック成功: "${path}" → 類似フィールドで代替`);
      return fallbackValue;
    }

    return directValue; // undefined を返す
  }

  /**
   * 類似フィールドを検索してフォールバック値を取得（構造化コンテンツ抽出機能付き）
   */
  private findSimilarField(obj: unknown, targetField: string): unknown {
    // 詳細データ構造分析を実行
    this.debugDataStructure(obj, `findSimilarField("${targetField}")の開始時`);
    
    if (!obj || typeof obj !== "object" || obj === null) {
      console.log(`❌ [MONO-X Debug] 無効なデータオブジェクト: ${typeof obj}`);
      return undefined;
    }

    const data = obj as Record<string, unknown>;
    const keys = Object.keys(data);

    // フィールドマッピング辞書（一般的な対応関係）
    const fieldMappings: Record<string, string[]> = {
      '全体要約': ['summary', 'Summary', '要約', 'サマリー', '概要', '内容', '商談内容', 'ヒアリング内容', 'content', 'Content'],
      'title': ['title', 'Title', 'タイトル', '件名', '会議目的', '訪問目的', 'meeting_purpose', 'purpose'],
      '要約': ['summary', 'Summary', '全体要約', '概要', '内容'],
      '内容': ['content', 'Content', '商談内容', 'ヒアリング内容', '全体要約']
    };

    // 🎯 特別処理: titleフィールドの場合、contentから抽出を試行
    if (targetField === 'title' || targetField.includes('タイトル')) {
      console.log(`🎯 [MONO-X Debug] title専用抽出プロセス開始`);
      const extractedTitle = this.extractTitleFromContent(data);
      if (extractedTitle) {
        this.debugFieldMapping(targetField, keys, extractedTitle, 'Markdownヘッダー抽出');
        return extractedTitle;
      }
    }

    // 🎯 特別処理: 全体要約フィールドの場合、contentから該当セクションを抽出
    if (targetField === '全体要約' || targetField.includes('要約')) {
      console.log(`🎯 [MONO-X Debug] 全体要約専用抽出プロセス開始`);
      const extractedSummary = this.extractSummaryFromContent(data);
      if (extractedSummary) {
        this.debugFieldMapping(targetField, keys, extractedSummary, 'Markdownセクション抽出');
        return extractedSummary;
      }
    }

    // 1. 完全一致の検索（大文字小文字を無視）
    console.log(`🔍 [MONO-X Debug] ステップ1: 完全一致検索`);
    const exactMatch = keys.find(key => key.toLowerCase() === targetField.toLowerCase());
    if (exactMatch && data[exactMatch]) {
      this.debugFieldMapping(targetField, keys, data[exactMatch], '完全一致');
      return data[exactMatch];
    }

    // 2. マッピング辞書による検索
    console.log(`🔍 [MONO-X Debug] ステップ2: マッピング辞書検索`);
    const mappedFields = fieldMappings[targetField] || [];
    console.log(`  📚 対象マッピング: [${mappedFields.join(', ')}]`);
    
    for (const mappedField of mappedFields) {
      const foundKey = keys.find(key => 
        key.toLowerCase() === mappedField.toLowerCase() ||
        key.includes(mappedField) || 
        mappedField.includes(key)
      );
      if (foundKey && data[foundKey]) {
        this.debugFieldMapping(targetField, keys, data[foundKey], `マッピング辞書経由 (${mappedField})`);
        return data[foundKey];
      }
    }

    // 3. 部分一致による検索
    console.log(`🔍 [MONO-X Debug] ステップ3: 部分一致検索`);
    const partialMatch = keys.find(key => {
      const keyLower = key.toLowerCase();
      const targetLower = targetField.toLowerCase();
      return keyLower.includes(targetLower) || targetLower.includes(keyLower);
    });
    if (partialMatch && data[partialMatch]) {
      this.debugFieldMapping(targetField, keys, data[partialMatch], '部分一致');
      return data[partialMatch];
    }

    // 4. キーワードベースの検索（「要約」関連の特別処理）
    if (targetField.includes('要約')) {
      console.log(`🔍 [MONO-X Debug] ステップ4: 要約キーワード検索`);
      const summaryKeys = keys.filter(key => 
        key.includes('要約') || 
        key.includes('summary') || 
        key.includes('Summary') ||
        key.includes('概要') ||
        key.includes('内容')
      );
      
      console.log(`  🔍 要約関連キー候補: [${summaryKeys.join(', ')}]`);
      
      for (const summaryKey of summaryKeys) {
        if (data[summaryKey]) {
          this.debugFieldMapping(targetField, keys, data[summaryKey], `要約キーワード経由 (${summaryKey})`);
          return data[summaryKey];
        }
      }
    }

    // 検索失敗の詳細ログ
    console.log(`❌ [MONO-X Debug] フィールド検索失敗`);
    console.log(`  🎯 対象フィールド: "${targetField}"`);
    console.log(`  📋 利用可能フィールド: [${keys.join(', ')}]`);
    console.log(`  🔍 試行した検索方法: 完全一致、マッピング辞書、部分一致、キーワード検索`);
    
    this.debugFieldMapping(targetField, keys, undefined, '全検索方法で失敗');
    return undefined;
  }

  /**
   * contentフィールドからtitleを抽出
   */
  private extractTitleFromContent(data: Record<string, unknown>): string | undefined {
    // contentまたはfullReportフィールドを検索
    const contentFields = ['content', 'Content', 'fullReport'];
    
    for (const fieldName of contentFields) {
      const content = data[fieldName];
      if (typeof content === 'string' && content.trim()) {
        console.log(`🔍 [MONO-X Debug] ${fieldName}からtitle抽出を試行...`);
        
        // パターン1: Markdownヘッダー（# タイトル）
        const markdownHeaderMatch = content.match(/^#\s+(.+)$/m);
        if (markdownHeaderMatch) {
          const title = markdownHeaderMatch[1].trim();
          console.log(`✅ [MONO-X Debug] Markdownヘッダーからtitle抽出: "${title}"`);
          return title;
        }
        
        // パターン2: 最初の非空行（Markdownヘッダーがない場合）
        const lines = content.split('\n');
        for (const line of lines) {
          const trimmedLine = line.trim();
          if (trimmedLine && 
              !trimmedLine.startsWith('#') && 
              !trimmedLine.startsWith('##') &&
              !trimmedLine.toLowerCase().includes('要約') &&
              !trimmedLine.toLowerCase().includes('summary')) {
            console.log(`✅ [MONO-X Debug] 最初の非空行からtitle抽出: "${trimmedLine}"`);
            return trimmedLine;
          }
        }
        
        // パターン3: 最初の行（改行まで）
        const firstLine = content.split('\n')[0]?.trim();
        if (firstLine) {
          console.log(`✅ [MONO-X Debug] 最初の行からtitle抽出: "${firstLine}"`);
          return firstLine;
        }
      }
    }
    
    console.log(`❌ [MONO-X Debug] title抽出失敗: 有効なcontentフィールドが見つかりません`);
    return undefined;
  }

  /**
   * contentフィールドから全体要約セクションを抽出
   */
  private extractSummaryFromContent(data: Record<string, unknown>): string | undefined {
    // contentまたはfullReportフィールドを検索
    const contentFields = ['content', 'Content', 'fullReport'];
    
    for (const fieldName of contentFields) {
      const content = data[fieldName];
      if (typeof content === 'string' && content.trim()) {
        console.log(`🔍 [MONO-X Debug] ${fieldName}から全体要約抽出を試行...`);
        
        // パターン1: ## 全体要約セクション
        const summaryMatch = content.match(/##\s*全体要約\s*\n([\s\S]*?)(?=\n##|\n#|$)/);
        if (summaryMatch) {
          const summary = summaryMatch[1].trim();
          console.log(`✅ [MONO-X Debug] "## 全体要約"セクションから抽出 (${summary.length}文字)`);
          return summary;
        }
        
        // パターン2: ## サマリー、## 概要など
        const altSummaryMatch = content.match(/##\s*(サマリー|概要|要約|まとめ|Summary)\s*\n([\s\S]*?)(?=\n##|\n#|$)/i);
        if (altSummaryMatch) {
          const summary = altSummaryMatch[2].trim();
          console.log(`✅ [MONO-X Debug] "## ${altSummaryMatch[1]}"セクションから抽出 (${summary.length}文字)`);
          return summary;
        }
        
        // パターン3: 全体要約と思われる段落を検索
        const paragraphs = content.split('\n\n');
        for (const paragraph of paragraphs) {
          const trimmedParagraph = paragraph.trim();
          if (trimmedParagraph.length > 50 && // 要約として十分な長さ
              (trimmedParagraph.includes('会議') || 
               trimmedParagraph.includes('本') || 
               trimmedParagraph.includes('目的') ||
               trimmedParagraph.includes('開催'))) {
            console.log(`✅ [MONO-X Debug] 推定要約段落から抽出 (${trimmedParagraph.length}文字)`);
            return trimmedParagraph;
          }
        }
        
        // パターン4: contentの最初の段落（他の方法で抽出できない場合）
        const firstParagraph = content.split('\n\n')[0]?.trim();
        if (firstParagraph && firstParagraph.length > 30) {
          console.log(`✅ [MONO-X Debug] 最初の段落をフォールバック要約として使用 (${firstParagraph.length}文字)`);
          return firstParagraph;
        }
      }
    }
    
    console.log(`❌ [MONO-X Debug] 全体要約抽出失敗: 有効なcontentフィールドが見つかりません`);
    return undefined;
  }

  /**
   * フィールド種別に応じた最大文字数を取得
   */
  private getFieldMaxLength(fieldPath: string, actualLength: number): number {
    // デフォルト制限値の大幅な拡張（従来の800文字から）
    const DEFAULT_MAX_LENGTH = 5000;
    
    // フィールド種別ごとの制限設定
    const fieldLimits: Record<string, number> = {
      // タイトル系：短めでOK
      'title': 200,
      'タイトル': 200,
      '件名': 200,
      '会議目的': 300,
      '訪問目的': 300,
      
      // 要約系：中程度
      '全体要約': 8000,
      '要約': 6000,
      'サマリー': 6000,
      '概要': 6000,
      
      // 詳細コンテンツ系：長め
      '内容': 10000,
      'content': 10000,
      'Content': 10000,
      '商談内容': 8000,
      'ヒアリング内容': 8000,
      '議論内容': 8000,
      '詳細': 10000,
      
      // 特定の業務フィールド：中程度
      '主要な議論と発言': 8000,
      '市場・競合分析': 6000,
      '製品・サービス戦略': 6000,
      '営業・販売戦略': 6000,
      'オペレーション・サプライチェーン': 6000,
      '課題と対策': 6000,
      '重要な数値・データ': 4000,
      '決定事項': 4000,
      'アクションアイテム': 6000,
      '重要な洞察・気づき': 6000,
      'リスク・懸念事項': 6000,
      '中長期戦略・展望': 6000,
      '次回会議・フォローアップ': 4000
    };
    
    // 完全一致でフィールド制限を検索
    let maxLength = fieldLimits[fieldPath];
    
    // 完全一致がない場合、部分一致で検索
    if (!maxLength) {
      for (const [pattern, limit] of Object.entries(fieldLimits)) {
        if (fieldPath.includes(pattern) || pattern.includes(fieldPath)) {
          maxLength = limit;
          console.log(`🔍 [MONO-X Debug] フィールド制限 部分一致: "${fieldPath}" → パターン"${pattern}" (${limit}文字)`);
          break;
        }
      }
    }
    
    // どのパターンにも一致しない場合はデフォルト値
    if (!maxLength) {
      maxLength = DEFAULT_MAX_LENGTH;
      console.log(`🔍 [MONO-X Debug] フィールド制限 デフォルト適用: "${fieldPath}" (${DEFAULT_MAX_LENGTH}文字)`);
    } else {
      console.log(`🔍 [MONO-X Debug] フィールド制限 個別設定: "${fieldPath}" (${maxLength}文字)`);
    }
    
    // 実際のコンテンツ長が制限を大幅に下回る場合、制限を緩和
    // （無駄に短く切り詰めることを防ぐ）
    if (actualLength > maxLength && actualLength < maxLength * 1.5) {
      const adjustedLength = Math.min(actualLength + 500, maxLength * 1.5);
      console.log(`🎯 [MONO-X Debug] 制限値調整: "${fieldPath}" ${maxLength} → ${adjustedLength}文字 (実際のコンテンツに合わせて調整)`);
      return adjustedLength;
    }
    
    return maxLength;
  }

  /**
   * デバッグ用: データ構造の詳細分析とログ出力
   * @param data 分析対象のデータ
   * @param context コンテキスト情報
   */
  private debugDataStructure(data: unknown, context: string): void {
    console.log(`🔍 [MONO-X Debug] データ構造分析 - ${context}`);
    
    if (!data) {
      console.log(`  📋 データ: null/undefined`);
      return;
    }
    
    if (typeof data !== 'object') {
      console.log(`  📋 データ型: ${typeof data}, 値: "${String(data).substring(0, 100)}..."`);
      return;
    }
    
    const dataObj = data as Record<string, unknown>;
    const keys = Object.keys(dataObj);
    
    console.log(`  📋 オブジェクト型データ: ${keys.length}個のキー`);
    console.log(`  📋 利用可能フィールド: [${keys.join(', ')}]`);
    
    // 各フィールドの詳細情報
    keys.forEach(key => {
      const value = dataObj[key];
      const valueType = typeof value;
      
      if (valueType === 'string') {
        const strValue = value as string;
        const preview = strValue.length > 100 ? 
          `${strValue.substring(0, 100)}...` : strValue;
        console.log(`    🔸 "${key}": string(${strValue.length}文字) = "${preview}"`);
      } else if (valueType === 'object' && value !== null) {
        const objKeys = Object.keys(value as Record<string, unknown>);
        console.log(`    🔸 "${key}": object(${objKeys.length}キー) = {${objKeys.join(', ')}}`);
      } else {
        console.log(`    🔸 "${key}": ${valueType} = ${String(value)}`);
      }
    });
  }

  /**
   * デバッグ用: フィールドマッピングプロセスの詳細追跡
   * @param targetField 対象フィールド名
   * @param availableFields 利用可能なフィールド一覧
   * @param foundValue 見つかった値
   * @param searchMethod 検索方法
   */
  private debugFieldMapping(
    targetField: string, 
    availableFields: string[], 
    foundValue: unknown, 
    searchMethod: string
  ): void {
    console.log(`🎯 [MONO-X Debug] フィールドマッピング詳細 - "${targetField}"`);
    console.log(`  📋 利用可能フィールド: [${availableFields.join(', ')}]`);
    console.log(`  🔍 検索方法: ${searchMethod}`);
    
    if (foundValue !== undefined) {
      const valueType = typeof foundValue;
      if (valueType === 'string') {
        const strValue = foundValue as string;
        console.log(`  ✅ 発見: ${valueType}(${strValue.length}文字)`);
        console.log(`  📝 プレビュー: "${strValue.substring(0, 150)}..."`);
      } else {
        console.log(`  ✅ 発見: ${valueType} = ${String(foundValue)}`);
      }
    } else {
      console.log(`  ❌ 未発見: このフィールドは見つかりませんでした`);
    }
  }

  /**
   * デバッグ用: 送信前データの最終検証とログ出力
   * @param submissionData MONO-X Oneに送信されるデータ
   * @param fieldMappings フィールドマッピング設定
   */
  private debugSubmissionData(
    submissionData: Record<string, unknown>, 
    fieldMappings: Array<{ targetFieldId: string; sourceField?: string }>
  ): void {
    console.log(`🚀 [MONO-X Debug] 送信データ最終検証`);
    console.log(`  📊 送信フィールド数: ${Object.keys(submissionData).length}`);
    
    // フィールドマッピングごとの検証
    fieldMappings.forEach(mapping => {
      const { targetFieldId, sourceField } = mapping;
      const value = submissionData[targetFieldId];
      
      console.log(`  🎯 フィールド: "${targetFieldId}" (ソース: "${sourceField || 'N/A'}")`);
      
      if (value === undefined || value === null) {
        console.log(`    ❌ 値: 空 - このフィールドはMONO-X Oneで検証エラーになる可能性があります`);
      } else if (typeof value === 'string') {
        const strValue = value as string;
        const trimmed = strValue.trim();
        
        if (trimmed.length === 0) {
          console.log(`    ❌ 値: 空文字列 - このフィールドはMONO-X Oneで検証エラーになる可能性があります`);
        } else {
          console.log(`    ✅ 値: string(${trimmed.length}文字) = "${trimmed.substring(0, 100)}..."`);
          
          // 切り詰めチェック
          if (strValue.endsWith('...')) {
            console.log(`    ⚠️  警告: この値は切り詰められている可能性があります（末尾に"..."があります）`);
          }
        }
      } else {
        console.log(`    ✅ 値: ${typeof value} = ${String(value)}`);
      }
    });
    
    // 送信データ全体のサマリー
    console.log(`  📋 送信データサマリー:`);
    Object.entries(submissionData).forEach(([key, value]) => {
      if (typeof value === 'string') {
        console.log(`    "${key}": ${(value as string).length}文字`);
      } else {
        console.log(`    "${key}": ${typeof value}`);
      }
    });
  }

  /**
   * コンテンツキーを取得
   * @param contentId コンテンツID
   * @returns キー情報オブジェクト
   */
  private async getContentKeys(
    contentId: number
  ): Promise<Record<string, string>> {
    if (!this.getToken()) {
      throw new Error("認証が必要です");
    }

    try {
      const response = await this.fetchWithTimeout(`${this.baseUrl}/api/content/keys`, {
        method: "POST",
        headers: this.getHeaders(true),
        body: JSON.stringify({ contentId }),
      });

      if (!response.ok) {
        throw new Error(
          `キー取得に失敗: ${response.status} ${response.statusText}`
        );
      }

      const result = await response.json();

      // レスポンス形式に応じて適切にキーオブジェクトを返す
      if (result && typeof result === "object") {
        return result;
      } else {
        // デフォルトキーを生成
        const defaultKeys = {
          STSTCD: `S${Date.now()}`,
          STJGCD: `G${Date.now()}`,
        };
        return defaultKeys;
      }
    } catch (error) {
      console.error("🔑 キー取得エラー:", error);
      // キー取得に失敗した場合はデフォルト値を使用
      return {
        STSTCD: `S${Date.now()}`,
        STJGCD: `G${Date.now()}`,
      };
    }
  }

  /**
   * 設定データを統一形式に正規化
   */
  private normalizeConfig(config: MonoXOneConfigForDB | MonoXOneMapping): {
    structuredInfoId: string;
    tenantName: string;
    pageId: number;
    contentId: number;
    fieldMappings: MonoXOneFieldMapping[];
  } {
    // tenantNameを正規化（完全URLの場合はドメイン部分を抽出）
    const normalizeTenantName = (tenantName: string): string => {
      if (
        tenantName.startsWith("http://") ||
        tenantName.startsWith("https://")
      ) {
        try {
          const url = new URL(tenantName);
          return url.hostname;
        } catch {
          return tenantName;
        }
      }
      return tenantName;
    };

    // 新形式（MonoXOneConfigForDB）の場合
    if ("targetSelection" in config && config.targetSelection) {
      return {
        structuredInfoId: config.structuredInfoId,
        tenantName: normalizeTenantName(config.tenantName),
        pageId: config.targetSelection.pageId,
        contentId: config.targetSelection.contentId,
        fieldMappings: config.fieldMappings || [],
      };
    }

    // 旧形式（MonoXOneMapping）の場合
    const legacyConfig = config as MonoXOneMapping;
    const fieldMappings: MonoXOneFieldMapping[] = [];

    // 旧形式では単一フィールドマッピングなので、配列形式に変換
    if (legacyConfig.inputId && legacyConfig.structuredFieldPath) {
      fieldMappings.push({
        structuredFieldPath: legacyConfig.structuredFieldPath,
        structuredFieldType: "string",
        structuredFieldDescription: legacyConfig.structuredFieldPath,
        monoXInputId: legacyConfig.inputId,
        monoXInputLabel: legacyConfig.inputLabel || "",
      });
    }

    // fieldMappingsプロパティがある場合はそれを優先
    if (
      legacyConfig.fieldMappings &&
      Array.isArray(legacyConfig.fieldMappings)
    ) {
      fieldMappings.push(...legacyConfig.fieldMappings);
    }

    return {
      structuredInfoId: legacyConfig.structuredInfoId,
      tenantName: normalizeTenantName(legacyConfig.tenantName),
      pageId: legacyConfig.pageId,
      contentId: legacyConfig.contentId,
      fieldMappings,
    };
  }

  /**
   * URLのクエリパラメータを修正
   * MONO-X One APIが返すURLで && が使用されている問題を修正
   */
  private fixUrlQueryParameters(url: string): string {
    try {
      // URLを分解
      const [baseUrl, queryString] = url.split('?');
      
      if (!queryString) {
        return url;
      }
      
      // && を & に置換
      const correctedQuery = queryString.replace(/&&/g, '&');
      
      // URLを再構築
      const correctedUrl = `${baseUrl}?${correctedQuery}`;
      
      // URLの妥当性を検証
      try {
        new URL(correctedUrl);
        return correctedUrl;
      } catch (validationError) {
        console.error("🔧 修正後URLが無効:", validationError);
        return url;
      }
      
    } catch (error) {
      console.error("🔧 URL修正エラー:", error);
      return url;
    }
  }

  /**
   * 安全にURIComponentをデコードする
   */
  private safeDecodeURIComponent(encodedString: string): string {
    try {
      return decodeURIComponent(encodedString);
    } catch (error) {
      console.warn('URIデコードエラー:', error);
      // デコードに失敗した場合は元の文字列を返す
      return encodedString;
    }
  }
}

// LocalStorage関連の関数は削除済み（DynamoDBベースの実装に移行）
// 認証情報の管理は、DynamoDBのstructured_informationテーブルのmono_x_one_configフィールドで行う

/**
 * APIクライアントインスタンスを作成
 */
export const createMonoXOneApiClient = (
  tenantName: string
): MonoXOneApiClient => {
  return new MonoXOneApiClient(tenantName);
};
